package com.oasisfeng.invaluable.social

/**
 * Localhost WebSocket client bus (browser side): connects to the [NodeSignalingHub] relay a Node form (TUI/MCP) hosts,
 * so browser tabs reach Node peers — and, via the relay's fan-out, each other. Messages are JSON strings. It reconnects
 * on drop, since the hosting Node process may come and go; until a hub is up, this bus simply carries no peers.
 *
 * Uses the browser-global `WebSocket`. Over `http://localhost` (or `file://`), `ws://127.0.0.1` is allowed; an
 * `https://` page blocks it as mixed content, so serve the bundle over http/localhost for cross-form discovery.
 */
class WebSocketBus(
    private val url: String,
    private val log: (String) -> Unit = {},
    private val reconnectMs: Int = 2000,
) : SignalingBus {
    private var socket: dynamic = null
    private var deliver: ((dynamic) -> Unit)? = null
    private var open = false
    private var closed = false

    override val label: String get() = url
    override val available: Boolean get() = js("typeof WebSocket !== 'undefined'") as Boolean

    override fun start(onMessage: (dynamic) -> Unit) {
        deliver = onMessage
        closed = false
        connect()
    }

    private fun connect() {
        if (closed) return
        val ws = js("Reflect").construct(js("WebSocket"), arrayOf<Any?>(url))
        socket = ws
        ws.onopen = fun(_: dynamic) { open = true; log("discovery: connected to hub $url") }
        ws.onmessage = fun(e: dynamic) { runCatching { deliver?.invoke(js("JSON.parse")(e.data)) } }
        ws.onclose = fun(_: dynamic) { open = false; socket = null; scheduleReconnect() }
        ws.onerror = fun(_: dynamic) { runCatching { ws.close() } }   // 'close' follows; reconnect there
    }

    private fun scheduleReconnect() {
        if (closed) return
        js("setTimeout")(fun() { if (!closed && socket == null) connect() }, reconnectMs)
    }

    override fun post(message: dynamic) {
        if (!open) return
        runCatching { socket.send(js("JSON.stringify")(message)) }
    }

    override fun close() {
        closed = true; open = false
        runCatching { socket.close() }; socket = null
    }
}
